/**
 * The looks a snap can be given, swiped through in the editor.
 *
 * Kept as a name with the snap, like its words and emojis, and applied when
 * it is shown -- so a clip takes one as readily as a photo, and nothing is
 * baked into the picture on the phone. The server keeps only a name from this
 * list (snapDecorOf).
 */
export const SNAP_FILTERS = [
  { name: 'warm', label: 'Warm', css: 'sepia(0.22) saturate(1.3) hue-rotate(-8deg) brightness(1.04)' },
  { name: 'cool', label: 'Cool', css: 'saturate(1.1) hue-rotate(12deg) brightness(1.03) contrast(1.04)' },
  { name: 'mono', label: 'Black & white', css: 'grayscale(1) contrast(1.12)' },
  { name: 'vintage', label: 'Vintage', css: 'sepia(0.45) contrast(0.9) brightness(1.06) saturate(0.85)' },
  { name: 'vivid', label: 'Vivid', css: 'saturate(1.6) contrast(1.1)' },
  { name: 'fade', label: 'Fade', css: 'contrast(0.82) brightness(1.1) saturate(0.75)' },
] as const;

export type SnapFilter = (typeof SNAP_FILTERS)[number]['name'];

export function isSnapFilter(name: unknown): name is SnapFilter {
  return SNAP_FILTERS.some((each) => each.name === name);
}

/** The CSS for a filter, or undefined for none. */
export function filterCss(name: string | undefined): string | undefined {
  return SNAP_FILTERS.find((each) => each.name === name)?.css;
}

/** The label to flash up, or 'Normal' for none. */
export function filterLabel(name: string | undefined): string {
  return SNAP_FILTERS.find((each) => each.name === name)?.label ?? 'Normal';
}

/**
 * The next look along, one way or the other, with no filter between the last
 * and the first, so swiping round always comes back to the picture as taken.
 */
export function nextFilter(now: SnapFilter | undefined, step: 1 | -1): SnapFilter | undefined {
  const names: Array<SnapFilter | undefined> = [undefined, ...SNAP_FILTERS.map((each) => each.name)];
  const at = names.indexOf(now);
  return names[(at + step + names.length) % names.length];
}
